import { Options } from 'ky';
import config from 'config';
import qs from 'qs';
import { getSortParam, downloadFile } from 'utils/api';
import {
  Customer,
  CustomerImportHistoryTableResult,
  CustomerPayloadFormValues,
  CustomerTableParams,
  CustomerTableResult,
} from 'Contacts/types';
import { SorterResult } from 'antd/es/table/interface';
import { TouchableRole } from 'Contacts/components/views/RolesTable';
import { Role, User, SaveUserPayload } from './types';
import { filterRoles, filterUsers, normalizeUserData } from './schemas';
import { useAuthenticatedApiClient } from '../../base';

const contactsClientOpts: Options = {
  prefixUrl: `${config.CORE_URL}/api/`,
};

const customJsonHeader = {
  'Content-Type': 'application/json',
};

const apiVersion13 = {
  'x-api-version': '13',
};

/* eslint-disable import/prefer-default-export */
export const useCustomersApi = () => {
  const authenticatedApiClient = useAuthenticatedApiClient();
  const customersApiClient = authenticatedApiClient.extend(contactsClientOpts);
  return {
    fetchCustomers: async ({
      sorter,
      search,
      pagination,
    }: CustomerTableParams) => {
      const params = {
        search,
        page: pagination?.current,
        limit: pagination?.pageSize,
        ...(sorter !== undefined && {
          // @ts-ignore
          ...(sorter.columnKey === 'name'
            ? // @ts-ignore
              { ordering: getSortParam(sorter as SorterResult<Customer>) }
            : // @ts-ignore
              { sort: getSortParam(sorter) }),
        }),
      };
      const paramsQs = qs.stringify(params, {
        addQueryPrefix: true,
        skipNulls: true,
        arrayFormat: 'comma',
      });
      const fullUrl = ['customers/', paramsQs].join('');
      return customersApiClient
        .get(fullUrl, {
          headers: apiVersion13,
        })
        .json<CustomerTableResult>();
    },
    saveCustomer: async (values: CustomerPayloadFormValues) => {
      const [url, method] =
        values.id !== undefined
          ? [`customers/${values.id}/`, 'PUT']
          : [`customers/`, 'POST'];
      if (method === 'POST') {
        return customersApiClient.post(url, {
          headers: { ...customJsonHeader, ...apiVersion13 },
          body: JSON.stringify(values),
        });
      }
      return customersApiClient.put(url, {
        headers: { ...customJsonHeader, ...apiVersion13 },
        body: JSON.stringify(values),
      });
    },
    removeCustomer: async (id: number) => {
      await customersApiClient.delete(`customers/${id}/`);
    },
    fetchCustomerImports: async () => {
      const imports = await customersApiClient
        .get('customers/import/')
        .json<CustomerImportHistoryTableResult>();
      return imports;
    },
    exportCustomersList: async (language: string) =>
      customersApiClient.post(
        `customers/export/?language=${language.toLowerCase()}`
      ),
    downloadTemplate: async (language: string) => {
      const template = await customersApiClient
        .get(
          `customers/import/template.xlsx?language=${language.toLowerCase()}`
        )
        .blob();

      downloadFile(template, 'customer-template', 'xlsx');
    },
    importCustomers: async (requestData: any) => {
      const form = new FormData();

      Object.keys(requestData).forEach((k) => {
        form.append(k, requestData[k]);
      });

      const response = await customersApiClient.post('customers/import/', {
        headers: {
          'x-api-Version': '8',
        },
        body: form,
      });
      return response.json();
    },
  };
};

/* eslint-disable import/prefer-default-export */
export const useContactsApi = () => {
  const authenticatedApiClient = useAuthenticatedApiClient();
  const contactsApiClient = authenticatedApiClient.extend(contactsClientOpts);
  return {
    fetchUsers: async (tableParams: any) => {
      const users = await contactsApiClient.get('users/').json<Array<User>>();
      if (tableParams && tableParams.search) {
        return filterUsers(users, tableParams.search);
      }
      return users;
    },
    removeUser: async (id: number) =>
      contactsApiClient.delete(`users/${id}/`, {
        headers: customJsonHeader,
      }),
    saveUser: async ({
      data,
      isFirstOwner,
    }: {
      data: SaveUserPayload;
      isFirstOwner: boolean;
    }) => {
      let values = data;
      if (values.id === undefined) {
        // adds random generated password to satisfy BE requirements
        // if creating a new user
        const pass = (Math.random() + 1).toString(36).substring(2, 22);
        values = {
          ...values,
          password: pass,
          password_confirmation: pass,
        };
      }

      const normalizedUserData = normalizeUserData({
        userData: values,
        addRoles: !isFirstOwner,
        addEmail: values.email,
      });

      const options = {
        headers: { ...customJsonHeader, 'x-api-version': '12' },
        body: JSON.stringify(normalizedUserData),
      };

      if (values.id) {
        return contactsApiClient.put(`users/${values.id}/`, options);
      }
      return contactsApiClient.post('users/', options);
    },
    fetchUserRoles: async (tableParams: any) => {
      const roles = await contactsApiClient
        .get('roles/')
        .json<{ data: Array<Role> }>();
      if (tableParams && tableParams.search) {
        return filterRoles(roles.data, tableParams.search);
      }
      return roles;
    },
    removeRole: async ({
      roleId,
      replacementId,
    }: {
      roleId: number;
      replacementId: number;
    }) =>
      contactsApiClient.delete(`roles/${roleId}/?replacement=${replacementId}`),
    saveRole: async (role: Role) => {
      if (role.id) {
        return contactsApiClient.put(`roles/${role.id}/`);
      }
      return contactsApiClient.post(`roles/`);
    },
    saveRoles: async (values: Array<TouchableRole> | Role) => {
      if (Array.isArray(values)) {
        return contactsApiClient.put(`roles/batch/`, {
          headers: customJsonHeader,
          body: JSON.stringify(values),
        });
      }
      if (values.id) {
        return contactsApiClient.put(`roles/${values.id}/`, {
          headers: customJsonHeader,
          body: JSON.stringify(values),
        });
      }
      return contactsApiClient.post(`roles/`, {
        headers: customJsonHeader,
        body: JSON.stringify(values),
      });
    },
  };
};
